import { useEffect, useState } from 'react';
import { generateAutocompleteItems } from '../utils/helper';
import { useLoader } from '../provider/LoaderProvider';
import useMenus from '../context/MenuContext';
import useParam from '../context/ParamContext';
import masterDataService from '../service/masterData.service';
import useUsers from '../context/UsersContext';
import { TagTarget, TagType } from '../config/tag';

export const useWorkspace = () => {
  const { showLoader, hideLoader } = useLoader();
  const { menus, getMenus, getMenuSubMenuId } = useMenus();
  const { params, getParam } = useParam();
  const { getUser, user } = useUsers();
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    user: [],
    project: [],
    status: [],
    taskType: [],
    taskStatus: [],
    taskPrio: [],
    projectType: [],
    company: [],
    companyLocation: [],
    crmTaskType: [],
    crmTaskStatus: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [projectTagLabels, setProjectTagLabels] = useState([]);

  useEffect(() => {
    getMenus();
    if (!Object.keys(params).length) getParam();
    if (!Object.keys(user).length) getUser();
    getProjectTags();
  }, []);

  useEffect(() => {
    if (!menus.length || !Object.keys(params).length) return;

    initDefaultValues();
  }, [menus, params]);

  useEffect(() => {
    if (autoCompleteData?.project?.length > 0) {
      const filteredProject = autoCompleteData.project.filter((p) => p.active);

      setAutoCompleteOptions((prev) => ({
        ...prev,
        project: generateAutocompleteItems(filteredProject, ['projectName', 'orderNumber'], 'projectId'),
      }));
    }
  }, [autoCompleteData.project]);

  const getProjectTags = () => {
    masterDataService.tagsByTargetToForm(TagTarget.PROJECT).then((data) => {
      const tagLabels = data.filter((item) => item.tagType === TagType.LABEL);
      setProjectTagLabels(tagLabels);
    });
  };

  const initDefaultValues = () => {
    const projectSubmenuId = getMenuSubMenuId('Project');
    const simpleTaskSubmenuId = getMenuSubMenuId('simpleTask');
    const crmTaskSubmenuId = getMenuSubMenuId('crmTask');

    const isProjectTypeStatus = params.PROJECT_SHOW_PROJECT_TYPE_STATUS === 'Y';

    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    if (isProjectTypeStatus) {
      promiseArray.push(
        new Promise((resolve, reject) => {
          masterDataService
            .projectTypeStatusToForm()
            .then((data) => {
              resolve(data);
            })
            .catch((e) => {
              reject(e);
            });
        })
      );
    } else {
      promiseArray.push(
        new Promise((resolve, reject) => {
          masterDataService
            .statusToForm(projectSubmenuId)
            .then((data) => {
              resolve(data);
            })
            .catch((e) => {
              reject(e);
            });
        })
      );
    }

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .typeToForm(simpleTaskSubmenuId)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .statusToForm(simpleTaskSubmenuId)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .priorityToForm(simpleTaskSubmenuId)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectTypeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyLocationToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .typeToForm(crmTaskSubmenuId)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .statusToForm(crmTaskSubmenuId)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    showLoader();

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          user: resp[0],
          project: resp[1],
          status: resp[2],
          taskType: resp[3],
          taskStatus: resp[4],
          taskPrio: resp[5],
          projectType: resp[6],
          company: resp[7],
          companyLocation: resp[8],
          crmTaskType: resp[9],
          crmTaskStatus: resp[10],
        };
        setAutoCompleteData(aData);

        const statusOptions = isProjectTypeStatus
          ? generateAutocompleteItems(aData.status, ['typeName', 'name'], 'id')
          : generateAutocompleteItems(aData.status.statusItems, 'itemName', 'itemId');

        const acOptions = {
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          project: [],
          status: statusOptions,
          taskType: generateAutocompleteItems(aData.taskType.typeItems, 'itemName', 'itemId'),
          taskStatus: generateAutocompleteItems(aData.taskStatus.statusItems, 'itemName', 'itemId'),
          taskPrio: generateAutocompleteItems(aData.taskPrio.priorityListItems, 'itemName', 'itemId'),
          projectType: generateAutocompleteItems(aData.projectType, 'name', 'id'),
          company: generateAutocompleteItems(aData.company, 'name', 'id'),
          companyLocation: generateAutocompleteItems(aData.companyLocation, 'name', 'id'),
          crmTaskType: generateAutocompleteItems(aData.crmTaskType.typeItems, 'itemName', 'itemId'),
          crmTaskStatus: generateAutocompleteItems(aData.crmTaskStatus.statusItems, 'itemName', 'itemId'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        hideLoader();
        setIsLoading(false);
      });
  };

  return {
    autoCompleteOptions,
    autoCompleteData,
    setAutoCompleteData,
    isLoading,
    projectTagLabels,
    setProjectTagLabels,
  };
};
